import { useState } from "react";
import { Controller, type Control, type UseFormRegister } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { WeddingQuoteInput } from "@/lib/plan-wizard-schema";

/** Optional preferences (#227), collapsed so the happy path stays short.
 * Extracted from WeddingQuoteBranch (#1221). */
export function QuotePreferencesFields({
  control,
  register,
}: {
  control: Control<WeddingQuoteInput>;
  register: UseFormRegister<WeddingQuoteInput>;
}) {
  // Progressive disclosure (#227): one tap reveals the optional fields.
  const [showPreferences, setShowPreferences] = useState(false);
  return (
    <>
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowPreferences((v) => !v)}
          className="text-sm font-medium text-primary hover:underline"
          aria-expanded={showPreferences}
        >
          {showPreferences ? "Hide preferences" : "Add preferences (optional)"}
        </button>
        {!showPreferences && (
          <p className="mt-1 text-xs text-muted-foreground">
            Service style, bar, dietary needs, start time — helps us quote faster.
          </p>
        )}
      </div>

      {showPreferences && (
        <div className="grid gap-4 rounded-lg border border-border/60 bg-muted/30 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="w-time">Start time</Label>
              <Input id="w-time" type="time" {...register("event_time")} />
            </div>
            <div className="grid gap-1.5">
              <Label id="w-bar-label">Bar service</Label>
              <Controller
                control={control}
                name="bar_service"
                render={({ field }) => (
                  <div className="flex gap-2" role="radiogroup" aria-labelledby="w-bar-label">
                    {(["yes", "no"] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        role="radio"
                        aria-checked={field.value === v}
                        onClick={() => field.onChange(field.value === v ? undefined : v)}
                        className={`inline-flex min-h-[40px] items-center rounded-full border px-4 py-1.5 text-sm transition-colors ${
                          field.value === v
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-background hover:border-primary/40"
                        }`}
                      >
                        {v === "yes" ? "Yes" : "No"}
                      </button>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label id="w-service-style-label">Service style</Label>
            <Controller
              control={control}
              name="service_style"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="w-service-style-label">
                  {(
                    [
                      ["plated", "Plated"],
                      ["family_style", "Family-style"],
                      ["buffet", "Buffet"],
                      ["stations", "Stations"],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={field.value === value}
                      onClick={() => field.onChange(field.value === value ? undefined : value)}
                      className={`inline-flex min-h-[40px] items-center rounded-full border px-4 py-1.5 text-sm transition-colors ${
                        field.value === value
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background hover:border-primary/40"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>
          <div className="grid gap-1.5">
            <Label id="w-dietary-label">Dietary needs</Label>
            <Controller
              control={control}
              name="dietary"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2" role="group" aria-labelledby="w-dietary-label">
                  {(
                    [
                      ["vegetarian", "Vegetarian"],
                      ["vegan", "Vegan"],
                      ["gluten_free", "Gluten-free"],
                      ["nut_free", "Nut-free"],
                    ] as const
                  ).map(([value, label]) => {
                    const selected = (field.value ?? []).includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() =>
                          field.onChange(
                            selected
                              ? (field.value ?? []).filter((d) => d !== value)
                              : [...(field.value ?? []), value],
                          )
                        }
                        className={`inline-flex min-h-[40px] items-center rounded-full border px-4 py-1.5 text-sm transition-colors ${
                          selected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-background hover:border-primary/40"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}
